import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail as MailIcon, ArrowLeft, FileText, Shield, AlertTriangle, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO'; 

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // State for privacy consent
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);

  // Jinshuju Form Links
  const JINSHUJU_CN = "https://ojtvjese.jsjform.com/f/tYXvbI";
  const JINSHUJU_EN = "https://ojtvjese.jsjform.com/f/RzJ5sS";
  const activeFormLink = currentLang === 'zh' ? JINSHUJU_CN : JINSHUJU_EN;

  // AI & Baidu Summary 
  const aiSummaryText = currentLang === 'zh' 
    ? "欧洲葡萄酒之旅 (EuroWineTrip) 博主联系页面。品牌合作、稿件投稿或读者交流，欢迎邮件联系。问卷由金数据提供技术支持，符合中国个人信息保护法要求。" 
    : "Contact page for EuroWineTrip blogger. Brand collaborations, submissions, or reader inquiries — email me. Survey powered by Jinshuju, complies with China's PIPL.";

  // Privacy Policy text for consent checkbox
  const privacyPolicyText = currentLang === 'zh' ? (
    <span>
      我已阅读并同意 <button 
        onClick={(e) => {
          e.stopPropagation();
          setShowPrivacyNotice(true);
        }} 
        className="underline hover:text-[#800020] font-medium transition-colors"
        type="button"
      >《隐私政策》</button>
    </span>
  ) : (
    <span>
      I have read and agree to the <button 
        onClick={(e) => {
          e.stopPropagation();
          setShowPrivacyNotice(true);
        }} 
        className="underline hover:text-[#800020] font-medium transition-colors"
        type="button"
      >Privacy Policy</button>
    </span>
  );

  // ===== 新增：ContactPage Schema =====
  const contactSchema = useMemo(() => {
    if (typeof window === 'undefined') return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": t('contact_page.sidebar_title'),
      "description": currentLang === 'zh' 
        ? "联系 EuroWineTrip 博主。品牌合作: eurowinetrip2026@foxmail.com" 
        : "Contact EuroWineTrip blogger. Collaborations: eurowinetrip2026@foxmail.com",
      "url": `${window.location.origin}/contact`,
      "mainEntity": {
        "@type": "Person",
        "name": "EuroWineTrip",
        "email": "eurowinetrip2026@foxmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Shanghai",
          "addressRegion": "CN-SH",
          "addressCountry": "CN"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": "eurowinetrip2026@foxmail.com",
          "availableLanguage": ["Chinese", "English"]
        },
        "sameAs": [
          // 如果有社交媒体链接，可以在这里添加
          // "https://instagram.com/yourpage",
          // "https://weibo.com/yourpage"
        ].filter(Boolean)
      }
    };
  }, [t, currentLang]);

  // ===== 可选：FAQ Schema（如果将来有常见问题可以扩展）=====
  // const faqSchema = useMemo(() => {
  //   return {
  //     "@context": "https://schema.org",
  //     "@type": "FAQPage",
  //     "mainEntity": [
  //       {
  //         "@type": "Question",
  //         "name": "How quickly do you respond to emails?",
  //         "acceptedAnswer": {
  //           "@type": "Answer",
  //           "text": "I typically respond within 24-48 hours."
  //         }
  //       }
  //     ]
  //   };
  // }, []);

  return (
    <main key={i18n.language} className="bg-cream min-h-screen flex flex-col">
      
      {/* SEO Component */}
      <SEO 
        title={t('contact_page.sidebar_title')} 
        description={currentLang === 'zh' 
          ? "联系 EuroWineTrip 博主。品牌合作: eurowinetrip2026@foxmail.com" 
          : "Contact EuroWineTrip blogger. Collaborations: eurowinetrip2026@foxmail.com"}
        keywords="联系博主, 葡萄酒, EuroWineTrip, 品牌合作, eurowinetrip2026@foxmail.com"
        slug="contact"
        type="website" 
        aiSummary={aiSummaryText}
        geoRegion="CN-SH"
        geoPlace="Shanghai"
        geoIcbm="31.2304, 121.4737"
      />

      {/* 新增：ContactPage Schema */}
      {contactSchema && (
        <script type="application/ld+json">
          {JSON.stringify(contactSchema)}
        </script>
      )}

      {/* 可选：FAQ Schema（预留）*/}
      {/* {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )} */}

      {/* Privacy Notice Modal */}
      {showPrivacyNotice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPrivacyNotice(false)}>
          <div className="bg-white rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowPrivacyNotice(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              ✕
            </button>
            <h2 className="text-2xl font-serif italic mb-4">
              {currentLang === 'zh' ? '隐私政策' : 'Privacy Policy'}
            </h2>
            <div className="space-y-4 text-gray-600">
              
              <div className="bg-olive/5 p-4 rounded-xl mb-4">
                <div className="flex items-start gap-2 mb-2">
                  <User size={18} className="text-olive mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium">
                    {currentLang === 'zh' 
                      ? '个人信息处理者：EuroWineTrip 个人博客' 
                      : 'Personal Information Handler: EuroWineTrip personal blog'}
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  {currentLang === 'zh' 
                    ? '独立运营 · 无商业实体' 
                    : 'Independently operated · No legal entity'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {currentLang === 'zh' 
                    ? '联系方式：eurowinetrip2026@foxmail.com' 
                    : 'Contact: eurowinetrip2026@foxmail.com'}
                </p>
              </div>

              {/* Data Flow Transparency */}
              <div className="bg-cream/30 p-4 rounded-xl border-l-4 border-amber-400">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium text-amber-700">
                    {currentLang === 'zh' ? '重要提示' : 'Important Notice'}
                  </p>
                </div>
                <p className="text-sm">
                  {currentLang === 'zh' 
                    ? '本博客不存储或访问您的任何个人信息。问卷数据直接提交至金数据平台，由北京亚杰时代软件技术有限公司处理并存储于中国境内服务器。' 
                    : 'This blog does NOT store or access your personal information. Survey data is submitted directly to Jinshuju platform, processed and stored by Beijing Yajie Times Software Co., Ltd. on servers within China.'}
                </p>
              </div>

              {/* 1. Data Collection */}
              <div className="bg-cream/30 p-4 rounded-xl">
                <h3 className="font-bold text-olive mb-2">
                  {currentLang === 'zh' ? '📋 信息收集与用途' : '📋 Data Collection & Purpose'}
                </h3>
                <p className="mb-3 text-sm">
                  {currentLang === 'zh' 
                    ? '为改进内容推荐，我通过金数据平台主要收集以下匿名信息（均为选填）：'
                    : 'To improve content recommendations, I mainly collect the following anonymous information via Jinshuju (all optional):'}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>
                    <span className="font-medium">{currentLang === 'zh' ? '年龄范围' : 'Age range'}</span>
                    <span className="text-gray-500 ml-2">
                      {currentLang === 'zh' ? '— 用于了解读者年龄段' : '— To understand reader demographics'}
                    </span>
                  </li>
                  <li>
                    <span className="font-medium">{currentLang === 'zh' ? '性别' : 'Gender'}</span>
                    <span className="text-gray-500 ml-2">
                      {currentLang === 'zh' ? '— 用于分析偏好' : '— To analyze preferences'}
                    </span>
                  </li>
                  <li>
                    <span className="font-medium">{currentLang === 'zh' ? '所在城市' : 'City'}</span>
                    <span className="text-gray-500 ml-2">
                      {currentLang === 'zh' ? '— 用于了解地域分布' : '— To understand geographic distribution'}
                    </span>
                  </li>
                </ul>
                
                <div className="mt-4 p-3 bg-amber-50/20 rounded-lg border border-amber-200">
                  <p className="text-xs flex items-start gap-1.5">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    {currentLang === 'zh' 
                      ? '本问卷不面向18周岁以下儿童。如发现儿童信息被提交，我将要求金数据立即删除。' 
                      : 'This survey is not intended for children under 18. If children\'s data is discovered, I will request Jinshuju to delete it immediately.'}
                  </p>
                </div>
              </div>

              {/* 2. Legal Basis */}
              <div>
                <h3 className="font-bold text-olive mb-2">
                  {currentLang === 'zh' ? '⚖️ 法律依据' : '⚖️ Legal Basis'}
                </h3>
                <p className="text-sm">
                  {currentLang === 'zh' 
                    ? '根据《中华人民共和国个人信息保护法》第十三条，我在获得您明确同意的基础上处理上述信息。' 
                    : 'In accordance with Article 13 of China\'s PIPL, I process the above information based on your explicit consent.'}
                </p>
                <p className="text-sm mt-2">
                  {currentLang === 'zh' 
                    ? '金数据（北京亚杰时代软件技术有限公司）作为数据处理方。该平台已通过信息安全等级保护三级认证。' 
                    : 'Jinshuju (Beijing Yajie Times Software Co., Ltd.) acts as the data processor. The platform holds MLPS Level 3 certification.'}
                </p>
              </div>

              {/* 3. Retention */}
              <div>
                <h3 className="font-bold text-olive mb-2">
                  {currentLang === 'zh' ? '⏳ 数据保留期限' : '⏳ Retention Period'}
                </h3>
                <p className="text-sm">
                  {currentLang === 'zh' 
                    ? '数据默认保留24个月。您可随时通过邮件联系要求提前删除。' 
                    : 'Data is retained for 24 months by default. You may request early deletion at any time via email.'}
                </p>
              </div>

              {/* 4. Your Rights */}
              <div>
                <h3 className="font-bold text-olive mb-2">
                  {currentLang === 'zh' ? '✅ 您的权利' : '✅ Your Rights'}
                </h3>
                <p className="text-sm">
                  {currentLang === 'zh' 
                    ? '您有权随时撤回同意。撤回后，我将停止收集新数据。如需删除已提交数据，请发送邮件至 eurowinetrip2026@foxmail.com。' 
                    : 'You may withdraw consent at any time. To delete submitted data, please email eurowinetrip2026@foxmail.com.'}
                </p>
              </div>

              {/* 5. Contact */}
              <div className="text-sm border-t pt-4 mt-2">
                <p className="flex items-center gap-2">
                  <MailIcon size={14} className="text-olive" />
                  {currentLang === 'zh' ? '隐私咨询：' : 'Privacy inquiries: '}
                  <a href="mailto:eurowinetrip2026@foxmail.com" className="text-olive hover:underline" itemProp="email">
                    eurowinetrip2026@foxmail.com
                  </a>
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {currentLang === 'zh' 
                    ? '最后更新：2026年2月' 
                    : 'Last updated: February 2026'}
                </p>
              </div>
            </div>
            <button 
              onClick={() => setShowPrivacyNotice(false)}
              className="mt-6 w-full bg-olive text-white px-6 py-4 rounded-full font-bold hover:bg-[#3D422C] transition-all"
            >
              {currentLang === 'zh' ? '我已了解' : 'I Understand'}
            </button>
          </div>
        </div>
      )}

      <div className="h-20 md:h-24 w-full" />
      
      <section className="container mx-auto px-4 max-w-5xl py-6 md:py-20">
        <nav aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-2 text-olive font-bold tracking-widest text-sm mb-6 md:mb-8 hover:text-[#800020] transition-colors">
            <ArrowLeft size={18} /> {t('category_page.back_home')}
          </Link>
        </nav>

        <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col md:flex-row border border-olive/5">
          <aside className="md:w-1/3 bg-olive p-8 md:p-10 text-white flex flex-col justify-between">
            <header>
              <h1 className="text-3xl font-serif italic mb-6" itemProp="name">{t('contact_page.sidebar_title')}</h1>
              <p className="opacity-80 mb-8 md:mb-10 text-base leading-relaxed" itemProp="description">
                {currentLang === 'zh' 
                  ? '品牌合作、稿件投稿或读者交流？欢迎邮件联系。问卷调查完全选填。' 
                  : 'Brand collaborations, submissions, or reader chat? Email me. Survey is optional.'}
              </p>
              <address className="not-italic space-y-6" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <div className="flex items-center gap-4 group">
                  <div className="p-2 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors">
                    <MailIcon size={20} aria-hidden="true" />
                  </div>
                  <a 
                    href="mailto:eurowinetrip2026@foxmail.com" 
                    className="text-lg md:text-base font-medium hover:underline break-all"
                    itemProp="email"
                  >
                    eurowinetrip2026@foxmail.com
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-white/10 rounded-full">
                    <MapPin size={20} aria-hidden="true" />
                  </div>
                  <span className="text-lg md:text-base font-medium" itemProp="addressLocality">{t('contact_page.location')}</span>
                  <meta itemProp="addressRegion" content="CN-SH" />
                  <meta itemProp="addressCountry" content="CN" />
                </div>
              </address>
            </header>
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-50 font-bold">
                <User size={12} />
                <span>{currentLang === 'zh' ? '个人博客' : 'Personal Blog'}</span>
              </div>
              <p className="text-[10px] tracking-widest opacity-30 mt-1">
                {currentLang === 'zh' ? '独立运营 · 用心分享' : 'Independent · Authentic'}
              </p>
            </div>
          </aside>

          <section className="md:w-2/3 p-6 md:p-16">
            <div className="mb-6 p-4 bg-olive/5 rounded-2xl border border-olive/20">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium text-amber-700">
                  {currentLang === 'zh' ? '数据流向说明' : 'Data Flow Notice'}
                </p>
              </div>
              <p className="text-sm text-olive/80">
                {currentLang === 'zh' 
                  ? '您提交的信息直接传输至金数据平台，本博客无法访问或存储任何个人数据。' 
                  : 'Your information is sent directly to Jinshuju. This blog cannot access or store your personal data.'}
              </p>
            </div>

            <div className="mb-8 p-4 bg-cream/50 rounded-2xl border border-olive/10">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={privacyConsent} 
                  onChange={(e) => setPrivacyConsent(e.target.checked)} 
                  className="mt-1 w-4 h-4 accent-olive cursor-pointer" 
                />
                <span className="text-sm text-olive/80 group-hover:text-olive transition-colors">
                  {privacyPolicyText}
                </span>
              </label>
              <div className="flex items-center gap-2 text-xs text-olive/40 mt-3">
                <Shield size={12} />
                <span>
                  {currentLang === 'zh' 
                    ? '金数据处理 · 加密传输 · 中国境内存储' 
                    : 'Processed by Jinshuju · Encrypted · Mainland China Storage'}
                </span>
              </div>
            </div>

            {privacyConsent ? (
              /* MOBILE SCROLL FIX: Added flex-col and overflow-y-auto wrapper */
              <div className="w-full h-[600px] bg-white rounded-xl overflow-hidden border border-olive/10 flex flex-col">
                <div className="bg-gradient-to-r from-olive/5 to-transparent p-3 border-b border-olive/10 flex-shrink-0">
                  <p className="text-xs text-olive/60 flex items-center gap-2">
                    <Shield size={12} />
                    {currentLang === 'zh' ? '金数据 · 加密传输' : 'Jinshuju · Encrypted'}
                  </p>
                </div>
                <div className="flex-grow overflow-y-auto overflow-x-hidden -webkit-overflow-scrolling-touch">
                  <iframe 
                    src={activeFormLink}
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    allowTransparency="true"
                    title="EuroWineTrip Survey"
                    className="min-h-full w-full"
                    style={{ border: 'none' }}
                  />
                </div>
              </div>
            ) : (
              <div className="w-full min-h-[300px] bg-cream/30 rounded-2xl border-2 border-dashed border-olive/20 flex items-center justify-center p-8 text-center">
                <div>
                  <FileText size={48} className="mx-auto text-olive/30 mb-4" />
                  <p className="text-olive/40 max-w-md mx-auto">
                    {currentLang === 'zh' 
                      ? '同意隐私政策后参与调查（年龄/性别/城市）' 
                      : 'Agree to privacy policy to take survey (age/gender/city)'}
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}